import { useEffect, useMemo, useState } from 'react'
import {
  Box, Drawer, IconButton, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { LineChart } from '@mui/x-charts/LineChart'
import { C100TickerTimeline } from '../../../_types/carnivore100.type'
import { Carnivore100Service } from '../../../services/carnivore100.service'
import { c100Tokens, C100Tokens } from '../c100-tokens'
import { TrendLabelCell } from '../signal-cells'
import {
  fmtPublishDate, fmtShortDate, fmtWeeks
} from '../../../pages/carnivore100/carnivore100.history.util'

const MONO = "'IBM Plex Mono', monospace"

interface Props {
  ticker: string | null // null = closed
  service: Carnivore100Service
  onClose: () => void
}

// One ticker's life on the list: when it arrived, how long it stayed, where it
// ranked each week, and what its score and trend read at each publish.
export default function TickerTimelinePanel({ ticker, service, onClose }: Props) {
  const theme = useTheme()
  const t = c100Tokens(theme)
  const [timeline, setTimeline] = useState<C100TickerTimeline | null>(null)
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)

  useEffect(() => {
    if (!ticker) return
    let active = true
    ;(async () => {
      setLoading(true)
      setErrored(false)
      setTimeline(null)
      const res = await service.getTickerHistory(ticker)
      if (!active) return
      if (res && !(res as any).errMsg) setTimeline(res as C100TickerTimeline)
      else setErrored(true)
      setLoading(false)
    })()
    return () => {
      active = false
    }
  }, [ticker, service])

  return (
    <Drawer
      anchor="right"
      open={!!ticker}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 440 }, bgcolor: t.page, backgroundImage: 'none' }
      }}
    >
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: MONO, fontSize: 20, fontWeight: 600, color: t.text }}>
              {ticker}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 12, color: t.muted }}>
              {timeline?.name ?? ' '}
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: t.muted }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {loading && (
          <>
            <Skeleton variant="rounded" height={64} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={180} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={200} />
          </>
        )}

        {!loading && errored && (
          <Typography sx={{ fontFamily: MONO, fontSize: 12, color: t.muted }}>
            Couldn't load this ticker's history.
          </Typography>
        )}

        {!loading && timeline && <TimelineBody timeline={timeline} t={t} />}
      </Box>
    </Drawer>
  )
}

function TimelineBody({ timeline, t }: { timeline: C100TickerTimeline; t: C100Tokens }) {
  const { points } = timeline
  const maxRank = useMemo(
    () => Math.max(33, ...points.map(p => p.rank ?? 0)),
    [points]
  )
  const newestFirst = useMemo(() => [...points].reverse(), [points])

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mb: 2 }}>
        <Stat label="First added" value={fmtPublishDate(timeline.firstAddedAt)} t={t} />
        <Stat label="Time on list" value={fmtWeeks(timeline.weeksOnList)} t={t} />
        <Stat
          label="Status"
          value={timeline.onListNow ? 'On the list' : 'Dropped'}
          sub={timeline.onListNow ? undefined : `last ${fmtShortDate(timeline.lastSeenAt)}`}
          color={timeline.onListNow ? t.gain : t.loss}
          t={t}
        />
      </Box>

      <SectionTitle t={t}>Rank by week</SectionTitle>
      <Box
        sx={{ bgcolor: t.card, border: `1px solid ${t.hairline}`, borderRadius: 2, mb: 2, px: 0.5 }}
      >
        {points.length < 2 ? (
          <Typography sx={{ fontFamily: MONO, fontSize: 12, color: t.muted, p: 2 }}>
            One published list so far — the chart fills in from next week.
          </Typography>
        ) : (
          <LineChart
            height={180}
            margin={{ top: 16, bottom: 28, left: 36, right: 16 }}
            series={[
              {
                data: points.map(p => p.rank),
                color: t.accent,
                connectNulls: false,
                curve: 'linear',
                valueFormatter: v => (v === null ? 'Off the list' : `#${v}`)
              }
            ]}
            xAxis={[
              {
                scaleType: 'point',
                data: points.map(p => fmtShortDate(p.publishedAt)),
                tickLabelStyle: { fontFamily: MONO, fontSize: 10, fill: t.muted }
              }
            ]}
            yAxis={[
              {
                // rank 1 is the top of the list, so it sits at the top
                reverse: true,
                min: 1,
                max: maxRank,
                tickLabelStyle: { fontFamily: MONO, fontSize: 10, fill: t.muted },
                valueFormatter: (v: number) => `#${v}`
              }
            ]}
            sx={{
              '.MuiChartsAxis-line, .MuiChartsAxis-tick': { stroke: t.hairline },
              '.MuiLineElement-root': { strokeWidth: 2 }
            }}
          />
        )}
      </Box>

      <SectionTitle t={t}>Week by week</SectionTitle>
      <Box sx={{ bgcolor: t.card, border: `1px solid ${t.hairline}`, borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small" aria-label={`${timeline.ticker} week by week`}>
          <TableHead>
            <TableRow>
              {['Published', 'Rank', 'Score', 'Trend'].map(h => (
                <TableCell
                  key={h}
                  sx={{
                    fontFamily: MONO,
                    fontSize: 10,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: t.muted,
                    borderBottom: `1px solid ${t.hairline}`
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {newestFirst.map(p => {
              const off = p.rank === null
              const cell = { fontFamily: MONO, fontSize: 12, borderBottom: `1px solid ${t.hairline}` }
              return (
                <TableRow key={p.version} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                  <TableCell sx={{ ...cell, color: t.text, whiteSpace: 'nowrap' }}>
                    {fmtShortDate(p.publishedAt)}
                  </TableCell>
                  <TableCell
                    sx={{ ...cell, color: off ? t.muted : t.text, fontWeight: off ? 400 : 600 }}
                  >
                    {off ? 'Off list' : `#${p.rank}`}
                  </TableCell>
                  <TableCell sx={{ ...cell, color: t.text, fontVariantNumeric: 'tabular-nums' }}>
                    {p.score === null ? '—' : p.score.toFixed(1)}
                  </TableCell>
                  <TableCell sx={cell}>
                    {off ? <Typography sx={{ color: t.muted, fontFamily: MONO, fontSize: 12 }}>—</Typography>
                      : <TrendLabelCell label={p.trendLabel} />}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Box>
    </>
  )
}

function Stat({ label, value, sub, color, t }: {
  label: string
  value: string
  sub?: string
  color?: string
  t: C100Tokens
}) {
  return (
    <Box sx={{ bgcolor: t.card, border: `1px solid ${t.hairline}`, borderRadius: 2, p: 1.25, minWidth: 0 }}>
      <Typography
        sx={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.muted }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, color: color ?? t.text, mt: 0.25 }}>
        {value}
      </Typography>
      {sub && <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: t.muted }}>{sub}</Typography>}
    </Box>
  )
}

function SectionTitle({ t, children }: { t: C100Tokens; children: string }) {
  return (
    <Typography
      component="h3"
      sx={{
        fontFamily: MONO,
        fontSize: 10,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: t.muted,
        mb: 0.75
      }}
    >
      {children}
    </Typography>
  )
}
